import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    Link,
    Navigate,
    useNavigate,
    useParams
} from 'react-router-dom';

import {
    Plane,
    TrainFront,
    Bus,
    CarFront,
    Clock,
    IndianRupee,
    MapPin,
    Search,
    ChevronRight
} from 'lucide-react';

import { CORRIDORS } from '../data/corridors';

import './TravelIntentPages.css';


const PAGE_CONFIG = {

    flight: {
        icon: Plane,
        label: 'Flight',

        title: (from, to) =>
            `${from} to ${to} Flights: Fares, Timings & Travel Options`,

        description: (from, to) =>
            `Compare flight options from ${from} to ${to}, including practical airport gateways, onward transfers and live date-specific availability.`,

        searchLabel: 'Search Flights',

        mode: 'Flight',
    },


    bus: {
        icon: Bus,
        label: 'Bus',

        title: (from, to) =>
            `${from} to ${to} Bus: Routes, Fares & Travel Options`,

        description: (from, to) =>
            `Compare bus options from ${from} to ${to}, including available arrival points, journey information and live date-specific inventory.`,

        searchLabel: 'Search Buses',

        mode: 'Bus',
    },


    compare: {
        icon: CarFront,
        label: 'Compare',

        title: (from, to) =>
            `${from} to ${to}: Flight vs Bus vs Train`,

        description: (from, to) =>
            `Compare flight, bus, train and mixed-mode options from ${from} to ${to}, including journey time, transfers and live availability.`,

        searchLabel: 'Compare All Options',

        mode: 'All',
    },

};


const iconFor = (type) => {

    if (type === 'flight') {
        return <Plane size={22} />;
    }

    if (type === 'train') {
        return <TrainFront size={22} />;
    }

    if (type === 'bus') {
        return <Bus size={22} />;
    }

    return <CarFront size={22} />;

};


const useDefaultDate = () =>
    useMemo(() => {

        const d = new Date();

        let daysUntilSat =
            (6 - d.getDay() + 7) % 7;

        if (daysUntilSat < 2) {
            daysUntilSat += 7;
        }

        d.setDate(
            d.getDate() + daysUntilSat
        );

        return d
            .toISOString()
            .split('T')[0];

    }, []);


const TravelIntentPage = ({ intent }) => {

    const { slug } = useParams();

    const navigate = useNavigate();

    const corridor = CORRIDORS[slug];

    const dateDefault = useDefaultDate();

    const [travelDate, setTravelDate] =
        useState(dateDefault);

    /*
     * Reference to the native date input.
     * Used to explicitly open the browser's
     * native date picker when the user clicks
     * anywhere on the date field.
     */
    const dateInputRef = useRef(null);


    const config = PAGE_CONFIG[intent];


    /*
     * Open native date picker.
     */
    const openDatePicker = () => {

        const input = dateInputRef.current;

        if (!input) {
            return;
        }

        /*
         * showPicker() opens the native date
         * picker in supported browsers.
         */
        if (
            typeof input.showPicker === 'function'
        ) {

            try {
                input.showPicker();
                return;
            } catch (error) {
                /*
                 * Some browsers may reject
                 * showPicker() if it isn't considered
                 * a direct user interaction.
                 */
            }

        }

        /*
         * Fallback for browsers that don't
         * support showPicker().
         */
        input.focus();

    };


    /*
     * SEO metadata
     */
    useEffect(() => {

        if (!corridor) {
            return;
        }


        const title = config.title(
            corridor.from.name,
            corridor.to.name
        );


        const description =
            config.description(
                corridor.from.name,
                corridor.to.name
            );


        document.title =
            `${title} | RouteChef`;


        const ensureMeta = (
            attr,
            key,
            content
        ) => {

            let el =
                document.head.querySelector(
                    `meta[${attr}="${key}"]`
                );


            if (!el) {

                el =
                    document.createElement(
                        'meta'
                    );

                el.setAttribute(
                    attr,
                    key
                );

                document.head.appendChild(
                    el
                );

            }


            el.setAttribute(
                'content',
                content
            );

        };


        ensureMeta(
            'name',
            'description',
            description
        );


        ensureMeta(
            'property',
            'og:title',
            `${title} | RouteChef`
        );


        ensureMeta(
            'property',
            'og:description',
            description
        );


        let canonical =
            document.head.querySelector(
                'link[rel="canonical"]'
            );


        const hadCanonical =
            !!canonical;


        const previousCanonical =
            canonical?.href;


        if (!canonical) {

            canonical =
                document.createElement(
                    'link'
                );

            canonical.setAttribute(
                'rel',
                'canonical'
            );

            document.head.appendChild(
                canonical
            );

        }


        canonical.href =
            `https://routechef.com/${
                intent === 'compare'
                    ? 'compare'
                    : intent
            }/${slug}`;


        const ld =
            document.createElement(
                'script'
            );


        ld.type =
            'application/ld+json';


        ld.id =
            'travel-intent-jsonld';


        ld.text =
            JSON.stringify({

                '@context':
                    'https://schema.org',

                '@type':
                    'WebPage',

                name:
                    title,

                description:
                    description,

                url:
                    canonical.href,

            });


        document.head.appendChild(
            ld
        );


        return () => {

            document
                .getElementById(
                    'travel-intent-jsonld'
                )
                ?.remove();


            if (!hadCanonical) {

                canonical.remove();

            } else if (
                previousCanonical
            ) {

                canonical.href =
                    previousCanonical;

            }

        };

    }, [
        corridor,
        config,
        intent,
        slug
    ]);


    if (!corridor) {

        return (
            <Navigate
                to="/"
                replace
            />
        );

    }


    const { from, to } =
        corridor;


    const modeOptions =
        corridor.options || [];


    const primaryOptions =
        intent === 'compare'
            ? modeOptions
            : modeOptions.filter(
                (opt) =>
                    opt.icon === intent
            );


    /*
     * Search URL
     */
    const searchUrl = (
        mode = config.mode
    ) => {

        const p =
            new URLSearchParams({

                from:
                    from.name,

                scode:
                    from.code || '',

                stype:
                    from.type || '',


                to:
                    to.name,

                dcode:
                    to.code || '',

                dtype:
                    to.type || '',


                date:
                    travelDate,


                adults:
                    1,

                children:
                    0,

                infants:
                    0,


                seatClasses:
                    'Economy,3A',


                modes:
                    mode,


                nonstop:
                    0,

            });


        if (!from.code) {

            p.set(
                'slat',
                from.lat
            );

            p.set(
                'slng',
                from.lng
            );

        }


        if (!to.code) {

            p.set(
                'dlat',
                to.lat
            );

            p.set(
                'dlng',
                to.lng
            );

        }


        return `/search-form?${p.toString()}`;

    };


    const Icon =
        config.icon;


    return (

        <div className="tip-page">

            {/* =====================================================
                HERO
            ===================================================== */}

            <header className="tip-hero">

                <nav
                    className="tip-breadcrumb"
                    aria-label="Breadcrumb"
                >

                    <Link to="/">
                        Home
                    </Link>

                    <ChevronRight
                        size={14}
                    />

                    <Link to="/how-to-reach">
                        How to Reach
                    </Link>

                    <ChevronRight
                        size={14}
                    />

                    <span>
                        {from.name} to {to.name}
                    </span>

                </nav>


                <div className="tip-intent-pill">

                    <Icon size={17} />

                    {config.label} search

                </div>


                <h1>

                    {config.title(
                        from.name,
                        to.name
                    )}

                </h1>


                <p className="tip-subtitle">

                    {config.description(
                        from.name,
                        to.name
                    )}

                </p>


                {/* =================================================
                    SEARCH CARD
                ================================================= */}

                <div className="tip-search-card">

                    <div className="tip-search-route">

                        <MapPin size={18} />

                        <strong>
                            {from.name}
                        </strong>

                        <ChevronRight
                            size={16}
                        />

                        <strong>
                            {to.name}
                        </strong>

                    </div>


                    <div className="tip-search-controls">

                        {/* Entire field opens native date picker */}
                        <div
                            className="tip-date-field"
                            onClick={
                                openDatePicker
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {

                                if (
                                    e.key ===
                                        'Enter' ||
                                    e.key === ' '
                                ) {

                                    e.preventDefault();

                                    openDatePicker();

                                }

                            }}
                            aria-label="Select travel date"
                        >

                            <input
                                ref={
                                    dateInputRef
                                }
                                type="date"
                                value={
                                    travelDate
                                }
                                min={
                                    new Date()
                                        .toISOString()
                                        .split('T')[0]
                                }
                                onChange={(e) =>
                                    setTravelDate(
                                        e.target.value
                                    )
                                }
                                aria-label="Travel date"
                            />

                        </div>


                        <button
                            onClick={() =>
                                navigate(
                                    searchUrl()
                                )
                            }
                        >

                            <Search size={17} />

                            {config.searchLabel}

                        </button>

                    </div>


                    <p>

                        Live inventory is checked
                        for your selected date.
                        Fares, schedules and
                        availability can change.

                    </p>

                </div>

            </header>


            {/* =====================================================
                MAIN CONTENT
            ===================================================== */}

            <main className="tip-content">


                {/* =================================================
                    OPTIONS
                ================================================= */}

                <section className="tip-section">

                    <h2>

                        {intent === 'flight'

                            ? `Flight options from ${from.name} to ${to.name}`

                            : intent === 'bus'

                            ? `Bus options from ${from.name} to ${to.name}`

                            : `Compare the ways to travel`

                        }

                    </h2>


                    <div className="tip-options">

                        {(
                            primaryOptions.length
                                ? primaryOptions
                                : modeOptions
                        ).map((opt) => (

                            <article
                                className={`tip-option ${opt.icon}`}
                                key={opt.id}
                            >

                                <div className="tip-option-head">

                                    <div className="tip-option-icon">

                                        {iconFor(
                                            opt.icon
                                        )}

                                    </div>


                                    <div>

                                        <h3>
                                            {opt.title}
                                        </h3>

                                        <span>
                                            {opt.badge}
                                        </span>

                                    </div>

                                </div>


                                <div className="tip-option-meta">

                                    <span>

                                        <Clock size={14} />

                                        {opt.duration}

                                    </span>


                                    <span>

                                        <IndianRupee
                                            size={14}
                                        />

                                        {opt.fareNote}

                                    </span>

                                </div>


                                <p>
                                    {opt.body}
                                </p>


                                <button
                                    onClick={() =>
                                        navigate(
                                            searchUrl(
                                                opt.modesParam ||
                                                    'All'
                                            )
                                        )
                                    }
                                >

                                    See live options

                                    <ChevronRight
                                        size={16}
                                    />

                                </button>

                            </article>

                        ))}

                    </div>

                </section>


                {/* =================================================
                    GATEWAYS
                ================================================= */}

                <section className="tip-section">

                    <h2>
                        Airports & railway gateways for{' '}
                        {to.name}
                    </h2>


                    <div className="tip-gateway-table-wrap">

                        <table className="tip-gateway-table">

                            <thead>

                                <tr>

                                    <th>
                                        Gateway
                                    </th>

                                    <th>
                                        Type
                                    </th>

                                    <th>
                                        Distance
                                    </th>

                                    <th>
                                        Transfer time
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {corridor.gateways.map(
                                    (g) => (

                                        <tr
                                            key={
                                                g.name
                                            }
                                        >

                                            <td>
                                                {g.name}
                                            </td>

                                            <td>
                                                {g.type}
                                            </td>

                                            <td>

                                                {
                                                    g.distanceKm ==
                                                    null

                                                        ? 'Destination point'

                                                        : `${g.distanceKm} km`

                                                }

                                            </td>

                                            <td>
                                                {g.cabTime}
                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    </div>

                </section>


                {/* =================================================
                    COMPLETE JOURNEY
                ================================================= */}

                <section className="tip-section">

                    <h2>
                        Why compare the complete journey?
                    </h2>


                    <p className="tip-copy">

                        RouteChef compares the route as
                        a journey rather than looking only
                        at the main transport leg. For
                        destinations away from an airport
                        or railway station, include the
                        onward road or local transfer when
                        comparing time and price.

                    </p>

                </section>


                {/* =================================================
                    FAQ
                ================================================= */}

                <section
                    className="tip-section tip-faq-section"
                >

                    <h2>
                        Frequently asked questions
                    </h2>


                    <div className="tip-faqs">

                        {corridor.faqs.map(
                            (f, i) => (

                                <details
                                    key={i}
                                    open={i === 0}
                                >

                                    <summary>
                                        {f.q}
                                    </summary>

                                    <p>
                                        {f.a}
                                    </p>

                                </details>

                            )
                        )}

                    </div>

                </section>


                {/* =================================================
                    FINAL CTA
                ================================================= */}

                <section className="tip-final-cta">

                    <h2>
                        Search {from.name} to {to.name}
                    </h2>


                    <p>

                        Check current{' '}

                        {config.label.toLowerCase()},

                        bus, train and
                        mixed-mode availability
                        for your date.

                    </p>


                    <button
                        onClick={() =>
                            navigate(
                                searchUrl()
                            )
                        }
                    >

                        <Search size={18} />

                        {config.searchLabel}

                    </button>


                    <Link
                        to={`/how-to-reach/${slug}`}
                    >

                        Read the complete
                        travel guide

                        <ChevronRight
                            size={15}
                        />

                    </Link>

                </section>

            </main>

        </div>

    );

};


export const FlightSeoPage = () => (
    <TravelIntentPage
        intent="flight"
    />
);


export const BusSeoPage = () => (
    <TravelIntentPage
        intent="bus"
    />
);


export const CompareSeoPage = () => (
    <TravelIntentPage
        intent="compare"
    />
);


export default TravelIntentPage;
